"use client";
import * as React from "react";
import { cn } from "@repo/utils/cn";
import { VariantProps } from "tailwind-variants";
import Image from "next/image";
import { SectionHeader, SectionHeaderProps } from "../SectionHeader";
import { sectionWrapper } from "../sectionWrappers";
import { Button, ButtonProps } from "../Button";

export interface MatchTeam {
  name: string;
  flagSrc?: string;
}

export interface MatchScheduleItemProps {
  teamA: MatchTeam;
  teamB: MatchTeam;
  stage: string;
  dayNumber: string;
  monthAbbr: string;
  reservationButton?: ButtonProps;
  _uid: string;
}

export interface MatchScheduleSectionProps
  extends VariantProps<typeof sectionWrapper> {
  typography?: SectionHeaderProps | null;
  matches?: MatchScheduleItemProps[];
  className?: string;
}

function FlagCircle({ team }: { team: MatchTeam }) {
  if (team.flagSrc) {
    return (
      <div className="uipm:relative uipm:w-[50px] uipm:h-[50px] uipm:shrink-0 uipm:overflow-hidden uipm:rounded-full">
        <Image
          src={team.flagSrc}
          alt={`${team.name} flag`}
          fill
          sizes="50px"
          className="uipm:object-cover"
        />
      </div>
    );
  }

  return (
    <div className="uipm:w-[50px] uipm:h-[50px] uipm:shrink-0 uipm:rounded-full uipm:bg-[var(--color-color-grey)] uipm:flex uipm:items-center uipm:justify-center uipm:overflow-hidden">
      <span className="uipm:text-white uipm:font-bold uipm:text-[34px] uipm:leading-[40px]">
        ?
      </span>
    </div>
  );
}

function MatchScheduleItem({
  teamA,
  teamB,
  stage,
  dayNumber,
  monthAbbr,
  reservationButton,
}: MatchScheduleItemProps) {
  return (
    <div className="uipm:border-b uipm:border-[var(--color-color-light)] uipm:pb-[var(--container-margin)] uipm:w-full">
      <div className="uipm:flex uipm:items-center uipm:gap-[var(--padding-padding-default)] uipm:w-full">
        {/* Date column */}
        <div className="uipm:w-[72px] uipm:shrink-0 uipm:flex uipm:flex-col uipm:items-center uipm:text-center uipm:pb-2 uipm:whitespace-nowrap">
          <span className="uipm:font-bold uipm:text-[28px] uipm:leading-[36px] uipm:text-[var(--typography-colors-text-body)]">
            {dayNumber}
          </span>
          <span className="uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:text-[var(--typography-colors-text-faded)]">
            {monthAbbr}
          </span>
        </div>

        {/* Game info */}
        <div className="uipm:flex uipm:flex-1 uipm:items-center uipm:gap-[32px] uipm:pl-[32px] uipm:min-w-0">
          {/* Flags */}
          <div className="uipm:flex uipm:items-start uipm:justify-center uipm:shrink-0">
            <FlagCircle team={teamA} />
            <FlagCircle team={teamB} />
          </div>

          {/* Match text */}
          <div className="uipm:flex uipm:flex-1 uipm:flex-col uipm:min-w-0">
            <span className="uipm:font-bold uipm:text-[18px] uipm:leading-[26px] uipm:text-[var(--typography-colors-text-body)]">
              {teamA.name} vs. {teamB.name}
            </span>
            <span className="uipm:font-medium uipm:text-(length:--typography-sizes-body-medium) uipm:leading-[var(--typography-line-heights-body-medium)] uipm:text-[var(--typography-colors-text-faded)]">
              {stage}
            </span>
          </div>
        </div>

        {/* Desktop button */}
        {reservationButton && (
          <div className="uipm:hidden uipm:md:block uipm:shrink-0 uipm:w-[158px]">
            <Button {...reservationButton} size="small" />
          </div>
        )}
      </div>

      {/* Mobile button */}
      {reservationButton && (
        <div className="uipm:md:hidden uipm:mt-3">
          <Button
            {...reservationButton}
            size="small"
            className={cn("uipm:w-full", reservationButton.className)}
          />
        </div>
      )}
    </div>
  );
}

export function MatchScheduleSection({
  typography,
  matches = [],
  className,
  backgroundColor = "none",
  topPadding = "default",
  bottomPadding = "default",
}: MatchScheduleSectionProps) {
  if (matches.length === 0 && !typography) return null;

  return (
    <section
      className={cn(
        sectionWrapper({
          backgroundColor,
          topPadding,
          bottomPadding,
        }),
        className,
      )}
    >
      <div className="uipm:max-w-[920px] uipm:mx-auto uipm:w-full">
        <div className="uipm:flex uipm:flex-col uipm:items-start">
          {typography && (
            <div className="uipm:pb-[var(--container-title-gap)] uipm:w-full">
              <SectionHeader {...typography} />
            </div>
          )}

          {matches.length > 0 && (
            <div className="uipm:border-t uipm:border-[var(--color-color-light)] uipm:flex uipm:flex-col uipm:gap-[var(--container-margin)] uipm:pt-[var(--container-gutter)] uipm:w-full">
              {matches.map((match) => (
                <MatchScheduleItem key={match._uid} {...match} />
              ))}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
